import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { BrandLogo, VeltaCredit } from "@/components/brand-logo";
import { Card, CardContent } from "@/components/ui/card";
import { APP_NAME, APP_VERSION } from "@/lib/app-info";
import { DOCS_PATH, docsPath } from "@/lib/docs-pages";

import { SettingsSection } from "./settings-section";

/** The docs, and pages of them worth a direct link from About, with their label's key. */
const DOC_LINKS = [
  { to: DOCS_PATH, label: "nav.docs" },
  { to: docsPath("connect-claude"), label: "settings.connectClaude" },
  { to: docsPath("privacy-and-data"), label: "settings.privacy" },
] as const;

/**
 * About: what this dashboard is, its version (package.json's, set at build
 * time), the way to the docs, and Velta's credit, which appears only here and
 * on the sign-in screen.
 */
export function AboutSettings() {
  const { t } = useTranslation();
  return (
    <SettingsSection title={t("nav.about")}>
      <Card>
        <CardContent className="space-y-6 pt-6">
          <BrandLogo variant="compact" className="h-8" />
          <div>
            <p className="font-heading text-primary text-lg font-bold">{APP_NAME}</p>
            <p className="text-muted mt-1 text-sm">{t("settings.aboutDescription")}</p>
          </div>
          <dl className="flex gap-3 text-sm">
            <dt className="text-muted">{t("settings.version")}</dt>
            <dd className="text-foreground font-mono tabular-nums">{APP_VERSION}</dd>
          </dl>
          <div className="border-border border-t pt-4">
            <p className="text-foreground mb-2 text-sm font-semibold">{t("settings.readMore")}</p>
            <ul className="space-y-1.5 text-sm pointer-coarse:space-y-0">
              {DOC_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-accent inline-flex items-center gap-1 font-medium hover:underline pointer-coarse:py-2.5"
                  >
                    {t(link.label)}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <VeltaCredit />
        </CardContent>
      </Card>
    </SettingsSection>
  );
}
